// The Submit specimen (DESIGN_SPEC §11.2 right column, §10.14, §12.15): the plate is the drop target; it shows the
// 1947 moth until a file is chosen, then that file's entropy portrait with PE section labels in the right gutter.
//   <SpecimenPortrait mode pick batch? specimenNo dragging url onActivate onCols />   batch: the moth stays, captioned with
//     the batch (n files · total size · archives)
//     pick: usePickFile().state · specimenNo: next job number (jobs + 1) or null while unknown
//     onActivate: open the file picker (click, Enter, Space on the plate) · onCols(240 | 120): portrait columns for this width
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import moth from "@/assets/specimen/moth-240.json";
import { Ascii, CopyButton, Hash, Museum, Plate, Specimen, type Art } from "@/components/ui";
import { bytes } from "@/lib/format";
import { layoutMarks } from "./annoLayout";
import { isArchiveName, type Batch } from "./batch";
import type { Mode } from "./inferProfile";
import { colsForWidth, pad4, portraitArt, portraitCaption } from "./portrait";
import type { PickState } from "./usePickFile";

const MOTH = moth as Art;

export function SpecimenPortrait({
  mode,
  pick,
  batch,
  specimenNo,
  dragging,
  url,
  onActivate,
  onCols,
}: {
  mode: Mode;
  pick: PickState;
  batch?: Batch | null;
  specimenNo: number | null;
  dragging: boolean;
  url: string;
  onActivate: () => void;
  onCols: (cols: number) => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<{ w: number; h: number } | null>(null);
  const [touched, setTouched] = useState(false);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const plate = wrap?.closest<HTMLElement>(".plate");
    if (!wrap || !plate) return;
    const measure = () => {
      const cs = getComputedStyle(plate);
      const w = plate.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const h = wrap.offsetHeight;
      setBox((b) => (b && Math.abs(b.w - w) < 0.5 && Math.abs(b.h - h) < 0.5 ? b : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(plate);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  const width = box?.w;
  useEffect(() => {
    if (width !== undefined) onCols(colsForWidth(width));
  }, [width, onCols]);

  useEffect(() => {
    if (pick.status !== "idle") setTouched(true);
  }, [pick.status]);

  const picked = mode === "file" && pick.status === "picked" ? pick : null;
  const portrait = picked?.analysis.portrait;
  const shown = useMemo(() => (portrait ? portraitArt(portrait) : null), [portrait]);
  const anno = useMemo(
    () => (portrait && shown && box?.h ? layoutMarks(portrait.marks, { rows: shown.art.rows, padTop: shown.padTop, fieldH: box.h }) : undefined),
    [portrait, shown, box?.h],
  );

  const scan = pick.status === "hashing" && mode === "file" ? pick.progress : touched ? "done" : "once";
  const next = specimenNo !== null ? pad4(specimenNo) : null;
  const title = next ? `Specimen ${next}` : "Specimen";

  let label: string;
  let museum: ReactNode;
  if (mode === "file" && batch) {
    const n = batch.files.length;
    const archives = batch.files.filter((f) => isArchiveName(f.name)).length;
    label = `A batch of ${n} ${n === 1 ? "file" : "files"}`;
    museum = (
      <Museum
        no="Batch"
        lbl={
          <span>
            {n} {n === 1 ? "file" : "files"} · {bytes(batch.files.reduce((s, f) => s + f.size, 0))}
            {archives > 0 && ` · ${archives} ${archives === 1 ? "archive" : "archives"}`}
          </span>
        }
      />
    );
  } else if (mode === "url") {
    label = `${title}: a URL, opened in the guest's browser`;
    museum = <Museum no={`${title} · url`} lbl={url ? <span className="break-all text-ink" translate="no">{url}</span> : <span>browser detonation</span>} />;
  } else if (picked && portrait) {
    label = `Entropy portrait of ${picked.file.name}`;
    museum = (
      <Museum
        no={title}
        lbl={portraitCaption(picked.file.name, picked.file.size, portrait, !!picked.analysis.pe)}
        more={
          <span className="inline-flex items-start gap-2">
            <span className="faint whitespace-nowrap pt-px">sha-256</span>
            <Hash value={picked.analysis.sha256} />
            <CopyButton value={picked.analysis.sha256} label="SHA-256" className="-mt-0.5" />
          </span>
        }
      />
    );
  } else if (pick.status === "hashing" && mode === "file") {
    label = `Reading ${pick.file.name}`;
    museum = (
      <Museum
        no={title}
        lbl={
          <span>
            {pick.file.name} · {bytes(pick.file.size)} · hashing <span className="num">{Math.round(pick.progress * 100)}%</span>
          </span>
        }
      />
    );
  } else if ((pick.status === "rejected" || pick.status === "failed") && mode === "file") {
    label = `${pick.file.name}, not pinned`;
    museum = <Museum no={title} lbl={<span>{pick.file.name} · {bytes(pick.file.size)} · not pinned</span>} />;
  } else {
    label = "Specimen 0001: the moth found in relay 70 of the Harvard Mark II in 1947, drawn in characters";
    museum = <Museum no="Specimen 0001" lbl={<span>Harvard Mark II · relay #70 · 9 Sep 1947</span>} />;
  }

  return (
    <Specimen label={label}>
      <Plate onActivate={onActivate} label="Choose a sample file" drag={dragging} anno={anno?.length ? anno : undefined}>
        <div ref={wrapRef}>
          <Ascii art={shown?.art ?? MOTH} scan={scan} />
        </div>
      </Plate>
      {museum}
    </Specimen>
  );
}
